import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Link, useHistory } from "react-router-dom";
import { withStyles } from "@mui/styles";

import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import FlagIcon from "@mui/icons-material/Flag";

import { AVAILABLE_MAPS, formatMapName } from "../../constants/maps";
import utils from "../../universal/utils";

const basePath = utils.getEdgeBasePath();

const styles = (theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    minHeight: "100vh",
    paddingTop: "2.5rem",
    backgroundColor: "#0b0b0b",
  },
  container: {
    width: "90%",
    maxWidth: "1400px",
    margin: "1.5rem auto",
    [theme.breakpoints.up("xl")]: {
      margin: "2rem auto",
    },
  },
  header: {
    display: "flex",
    alignItems: "center",
    marginBottom: "2rem",
    gap: "1rem",
  },
  title: {
    fontSize: "2rem",
    fontWeight: 700,
    color: "#ffffff",
    display: "flex",
    alignItems: "center",
    gap: "0.75rem",
    [theme.breakpoints.up("xl")]: {
      fontSize: "2.5rem",
    },
  },
  titleIcon: {
    fontSize: "2rem",
    color: "#E8A33D",
    [theme.breakpoints.up("xl")]: {
      fontSize: "2.5rem",
    },
  },
  filtersContainer: {
    backgroundColor: "#1C1E20",
    borderRadius: "12px",
    padding: "1.5rem",
    marginBottom: "2rem",
    border: "1px solid rgba(232, 163, 61, 0.2)",
  },
  filtersTitle: {
    fontSize: "1.1rem",
    fontWeight: 600,
    color: "#ffffff",
    marginBottom: "1rem",
  },
  filtersRow: {
    display: "flex",
    gap: "1rem",
    flexWrap: "wrap",
    alignItems: "flex-end",
  },
  filterControl: {
    minWidth: "180px",
    "& .MuiOutlinedInput-root": {
      backgroundColor: "#0b0b0b",
      "& fieldset": {
        borderColor: "rgba(232, 163, 61, 0.3)",
      },
      "&:hover fieldset": {
        borderColor: "rgba(232, 163, 61, 0.5)",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#E8A33D",
      },
    },
    "& .MuiInputLabel-root": {
      color: "rgba(255, 255, 255, 0.7)",
      "&.Mui-focused": {
        color: "#E8A33D",
      },
    },
    "& .MuiSelect-select": {
      color: "#ffffff",
    },
    "& .MuiSelect-icon": {
      color: "#E8A33D",
    },
  },
  quickFilters: {
    display: "flex",
    gap: "0.5rem",
    flexWrap: "wrap",
  },
  quickFilterButton: {
    backgroundColor: "#0b0b0b",
    color: "#ffffff",
    border: "1px solid rgba(232, 163, 61, 0.3)",
    borderRadius: "8px",
    padding: "0.5rem 1rem",
    fontSize: "0.875rem",
    textTransform: "none",
    "&:hover": {
      backgroundColor: "rgba(232, 163, 61, 0.1)",
      borderColor: "#E8A33D",
    },
  },
  quickFilterButtonActive: {
    backgroundColor: "#E8A33D",
    borderColor: "#E8A33D",
    "&:hover": {
      backgroundColor: "#e66a00",
    },
  },
  tableContainer: {
    backgroundColor: "#1C1E20",
    borderRadius: "12px",
    border: "1px solid rgba(232, 163, 61, 0.2)",
  },
  tableHead: {
    backgroundColor: "rgba(232, 163, 61, 0.1)",
    "& th": {
      color: "#ffffff",
      fontWeight: 600,
      fontSize: "0.95rem",
    },
  },
  tableRow: {
    cursor: "pointer",
    transition: "background-color 0.2s ease",
    "&:hover": {
      backgroundColor: "rgba(232, 163, 61, 0.05)",
    },
    "& td": {
      color: "#ffffff",
      borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
    },
  },
  statusChip: {
    fontWeight: 600,
    fontSize: "0.75rem",
  },
  statusSubmitted: {
    backgroundColor: "rgba(59, 130, 246, 0.2)",
    color: "#60a5fa",
  },
  statusInReview: {
    backgroundColor: "rgba(251, 191, 36, 0.2)",
    color: "#fbbf24",
  },
  statusResolved: {
    backgroundColor: "rgba(34, 197, 94, 0.2)",
    color: "#4ade80",
  },
  statusConcluded: {
    backgroundColor: "rgba(168, 85, 247, 0.2)",
    color: "#a855f7",
  },
  reportTypeBadge: {
    fontSize: "0.75rem",
    padding: "0.125rem 0.5rem",
    borderRadius: "4px",
    fontWeight: 600,
  },
  myReport: {
    backgroundColor: "rgba(34, 197, 94, 0.2)",
    color: "#4ade80",
  },
  aboutMe: {
    backgroundColor: "rgba(239, 68, 68, 0.2)",
    color: "#f87171",
  },
  offensesCell: {
    display: "flex",
    flexWrap: "wrap",
    gap: "0.25rem",
  },
  offenseChip: {
    fontSize: "0.7rem",
    height: "20px",
  },
  emptyState: {
    textAlign: "center",
    padding: "4rem 2rem",
  },
  emptyStateIcon: {
    fontSize: "4rem",
    color: "rgba(232, 163, 61, 0.3)",
    marginBottom: "1rem",
  },
  emptyStateText: {
    fontSize: "1.2rem",
    color: "rgba(255, 255, 255, 0.6)",
  },
  loading: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: "4rem",
  },
});

function ReportsPage({ classes, actions, catalog }) {
  const history = useHistory();
  const { user } = catalog || {};

  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [totalReports, setTotalReports] = useState(0);

  // Filters
  const [mapFilter, setMapFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [quickTimeFilter, setQuickTimeFilter] = useState("all");

  useEffect(() => {
    if (user) {
      fetchReports();
    }
  }, [user, page, rowsPerPage, mapFilter, statusFilter, quickTimeFilter]);

  const fetchReports = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: (page + 1).toString(),
        limit: rowsPerPage.toString(),
        reportType: "all"
      });

      if (mapFilter && mapFilter !== "all") {
        params.append("map", mapFilter);
      }

      if (statusFilter && statusFilter !== "all") {
        params.append("status", statusFilter);
      }

      // Add date range based on quick filter
      if (quickTimeFilter !== "all") {
        const now = new Date();
        let dateFrom;

        switch (quickTimeFilter) {
          case "today":
            dateFrom = new Date(now.getFullYear(), now.getMonth(), now.getDate());
            break;
          case "last_week":
            dateFrom = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
            break;
          case "this_week":
            const dayOfWeek = now.getDay();
            const diff = now.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1);
            dateFrom = new Date(now.setDate(diff));
            dateFrom.setHours(0, 0, 0, 0);
            break;
          case "this_month":
            dateFrom = new Date(now.getFullYear(), now.getMonth(), 1);
            break;
          case "last_month":
            dateFrom = new Date(now.getFullYear(), now.getMonth() - 1, 1);
            const dateTo = new Date(now.getFullYear(), now.getMonth(), 0);
            params.append("dateTo", dateTo.toISOString());
            break;
          case "last_year":
            dateFrom = new Date(now.getFullYear() - 1, 0, 1);
            break;
        }

        if (dateFrom) {
          params.append("dateFrom", dateFrom.toISOString());
        }
      }

      const response = await fetch(`${basePath}/api/v1/reports/user?${params}`, {
        credentials: "include",
      });

      const data = await response.json();

      if (data.ok) {
        setReports(data.reports);
        setTotalReports(data.pagination.total);
      } else {
        console.error("Error fetching reports:", data.message);
      }
    } catch (error) {
      console.error("Error fetching reports:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRowClick = (reportId) => {
    history.push(`/reports/${reportId}`);
  };

  const getStatusClass = (status) => {
    switch (status) {
      case "submitted":
        return classes.statusSubmitted;
      case "in_review":
        return classes.statusInReview;
      case "resolved":
        return classes.statusResolved;
      case "concluded":
        return classes.statusConcluded;
      default:
        return classes.statusSubmitted;
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  const formatStatusLabel = (status) => {
    return status.replace(/_/g, " ").toUpperCase();
  };

  if (!user) {
    return (
      <div className={classes.root}>
        <div className={classes.container}>
          <Typography variant="h4" className={classes.title}>
            Please log in to view reports
          </Typography>
        </div>
      </div>
    );
  }

  return (
    <div className={classes.root}>
      <div className={classes.container}>
        <div className={classes.header}>
          <IconButton
            onClick={() => history.push("/lobby")}
            style={{ color: "#E8A33D" }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography className={classes.title}>
            <FlagIcon className={classes.titleIcon} />
            My Reports
          </Typography>
        </div>

        <Paper className={classes.filtersContainer} elevation={0}>
          <Typography className={classes.filtersTitle}>Filters</Typography>
          <div className={classes.filtersRow}>
            <FormControl className={classes.filterControl} size="small">
              <InputLabel>Map</InputLabel>
              <Select
                value={mapFilter}
                label="Map"
                onChange={(e) => setMapFilter(e.target.value)}
              >
                <MenuItem value="all">All Maps</MenuItem>
                {AVAILABLE_MAPS.map((map) => (
                  <MenuItem key={map} value={map}>
                    {formatMapName(map)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl className={classes.filterControl} size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="all">All Statuses</MenuItem>
                <MenuItem value="submitted">Submitted</MenuItem>
                <MenuItem value="in_review">In Review</MenuItem>
                <MenuItem value="resolved">Resolved</MenuItem>
                <MenuItem value="concluded">Concluded</MenuItem>
              </Select>
            </FormControl>

            <Box style={{ flex: 1 }}>
              <Typography style={{ color: "#ffffff", fontSize: "0.875rem", marginBottom: "0.5rem" }}>
                Time Period
              </Typography>
              <div className={classes.quickFilters}>
                {[
                  { value: "all", label: "All Time" },
                  { value: "today", label: "Today" },
                  { value: "last_week", label: "Last 7 Days" },
                  { value: "this_week", label: "This Week" },
                  { value: "this_month", label: "This Month" },
                  { value: "last_month", label: "Last Month" },
                  { value: "last_year", label: "Last Year" },
                ].map((filter) => (
                  <Button
                    key={filter.value}
                    className={`${classes.quickFilterButton} ${
                      quickTimeFilter === filter.value ? classes.quickFilterButtonActive : ""
                    }`}
                    onClick={() => setQuickTimeFilter(filter.value)}
                  >
                    {filter.label}
                  </Button>
                ))}
              </div>
            </Box>
          </div>
        </Paper>

        <Paper className={classes.tableContainer} elevation={0}>
          {loading ? (
            <div className={classes.loading}>
              <CircularProgress style={{ color: "#E8A33D" }} />
            </div>
          ) : reports.length === 0 ? (
            <div className={classes.emptyState}>
              <FlagIcon className={classes.emptyStateIcon} />
              <Typography className={classes.emptyStateText}>
                No reports found matching your filters
              </Typography>
            </div>
          ) : (
            <>
              <TableContainer>
                <Table>
                  <TableHead className={classes.tableHead}>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Match</TableCell>
                      <TableCell>Player</TableCell>
                      <TableCell>Offenses</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {reports.map((report) => (
                      <TableRow
                        key={report.reportId}
                        className={classes.tableRow}
                        onClick={() => handleRowClick(report.reportId)}
                      >
                        <TableCell>{formatDate(report.createdAt)}</TableCell>
                        <TableCell>
                          <Chip
                            label={report.isMyReport ? "My Report" : "About Me"}
                            size="small"
                            className={`${classes.reportTypeBadge} ${
                              report.isMyReport ? classes.myReport : classes.aboutMe
                            }`}
                          />
                        </TableCell>
                        <TableCell>
                          {report.map ? formatMapName(report.map) : "Unknown"}
                        </TableCell>
                        <TableCell>
                          {report.isMyReport
                            ? report.reportedNickname
                            : report.reporterNickname}
                        </TableCell>
                        <TableCell>
                          <div className={classes.offensesCell}>
                            {report.offenses.slice(0, 3).map((offense) => (
                              <Chip
                                key={offense}
                                label={offense.replace(/_/g, " ")}
                                size="small"
                                className={classes.offenseChip}
                              />
                            ))}
                            {report.offenses.length > 3 && (
                              <Chip
                                label={`+${report.offenses.length - 3}`}
                                size="small"
                                className={classes.offenseChip}
                              />
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={formatStatusLabel(report.status)}
                            className={`${classes.statusChip} ${getStatusClass(report.status)}`}
                            size="small"
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                rowsPerPageOptions={[10, 20, 50]}
                component="div"
                count={totalReports}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                style={{ color: "#ffffff" }}
              />
            </>
          )}
        </Paper>
      </div>
    </div>
  );
}

ReportsPage.propTypes = {
  classes: PropTypes.object.isRequired,
  actions: PropTypes.object.isRequired,
  catalog: PropTypes.object.isRequired,
};

export default withStyles(styles)(ReportsPage);
